/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Zap } from 'lucide-react';
import React from 'react';
import { getDateLocale } from 'shared/i18n';
import { AnalyticsFastestGoalRecord } from 'modules/analytics/domain/interfaces/tournament-analytics';

interface FastestGoalCardProps {
  record: AnalyticsFastestGoalRecord | null;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceWhite};
  border: 1px solid ${({ theme }) => theme.colors.SurfacePlatinum20};
  border-left: 4px solid ${({ theme }) => theme.colors.DarkYellow};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 14px;
  font-weight: 600;

  svg {
    color: ${({ theme }) => theme.colors.DarkYellow};
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.Gray};
  font-size: 14px;
  text-align: center;
  padding: 12px 0;
`;

const PlayerName = styled.div`
  color: ${({ theme }) => theme.colors.DarkBlue};
  font-size: 20px;
  font-weight: 700;
  text-align: center;
`;

const Footer = styled.div`
  color: ${({ theme }) => theme.colors.WhiteGray};
  font-size: 12px;
  text-align: center;
`;

const FastestGoalCard = React.memo(({ record }: FastestGoalCardProps) => {
  const { i18n } = useLingui();

  if (record === null) {
    return (
      <Card>
        <TitleRow>
          <Zap size={20} />
          <span>
            <Trans>Самый быстрый гол</Trans>
          </span>
        </TitleRow>
        <EmptyState>
          <Trans>Нет данных за выбранный период</Trans>
        </EmptyState>
      </Card>
    );
  }

  const { goalSecond } = record;

  return (
    <Card>
      <TitleRow>
        <Zap size={20} />
        <span>
          <Trans>Самый быстрый гол</Trans>
        </span>
      </TitleRow>
      <PlayerName>{record.playerName}</PlayerName>
      <Footer>
        {record.championshipName} · {format(new Date(record.matchDate), 'd MMM yyyy', { locale: getDateLocale(i18n.locale) })} ·{' '}
        <Trans>{goalSecond} сек.</Trans>
      </Footer>
    </Card>
  );
});

export default FastestGoalCard;
